import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { css } from '@emotion/react';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import GoalItem from 'athena/components/goal-item';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { config } from '@config/pendo.config.json';
import { useMentoringProgramGoals } from 'athena/hooks/use-mentoring-program-goals';
import useConnection from 'athena/hooks/use-connection';
import {
  getMentoringProgramGoalItems,
} from 'redux/selectors/mentorship-program-goal-items';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { halfSpacing, largeSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

type EnrollmentsMap = { [id: number]: MentoringProgramEnrollment };
import { fadeInAnimation } from './styles';
import { gray6 } from 'styles/global_defaults/colors';
import { ReactComponent as GoalsSectionIcon } from 'styles/icons/functional-upskilling.svg';

const INITIAL_VISIBLE_COUNT = 3;
const MAX_VISIBLE_COUNT = 6;

const styles = css`
  ${fadeInAnimation};
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: ${standardSpacing}px;
  padding: ${largeSpacing}px;
  gap: 12px;

  .goals-header {
    height: 52px;
    gap: 4px;
  }

  .goals-list {
    border-top: 1px solid ${gray6};
    padding-top: ${threeQuartersSpacing}px;
    gap: ${halfSpacing}px;
  }
`;

const ReflectionGoals = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: currentEnrollmentId,
  } = mentoringProgram || {};
  const { connection, mentorshipProgramEnrollment } = useConnection(currentEnrollmentId);
  const connectionId = connection?.connectionId;
  const { id: connectionEnrollmentId } = connection?.assignedEnrollment || {};

  const { ref: goalsEndRef, inView: isGoalsEndReached } = useInView();
  const { goalItems, loadMore } = useMentoringProgramGoals({ mentorshipProgramId, connectionId, statusSort: 'desc' });

  useEffect(() => {
    if (!isExpanded) return;

    if (isGoalsEndReached) {
      loadMore();
    }
  }, [isExpanded, isGoalsEndReached, loadMore]);

  const { totalCount } = useSelector(getMentoringProgramGoalItems(connectionId)) || {};

  const enrollments: EnrollmentsMap | undefined = (mentorshipProgramEnrollment && connection?.assignedEnrollment)
    ? {
      [currentEnrollmentId!]: mentorshipProgramEnrollment,
      [connectionEnrollmentId!]: connection.assignedEnrollment as MentoringProgramEnrollment,
    }
    : undefined;

  const goals = goalItems ?? [];

  useLayoutEffect(() => {
    if (!isExpanded || !listRef.current) {
      setListMaxHeight(null);
      return;
    }

    const children = Array.from(listRef.current.children);
    const itemCount = children.length - 1; // exclude sentinel div

    if (itemCount <= MAX_VISIBLE_COUNT) {
      setListMaxHeight(null);
      return;
    }

    const listEl = listRef.current;
    const listRect = listEl.getBoundingClientRect();
    const nthItem = children[MAX_VISIBLE_COUNT - 1] as HTMLElement;
    const nthItemRect = nthItem.getBoundingClientRect();
    setListMaxHeight(nthItemRect.bottom - listRect.top + listEl.scrollTop);
  }, [isExpanded, goals.length]);

  if (!goals.length) return null;

  const total = totalCount ?? goals.length;
  const hasMore = total > INITIAL_VISIBLE_COUNT;
  const visibleGoals = isExpanded ? goals : goals.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = total - INITIAL_VISIBLE_COUNT;

  const handleToggle = () => setIsExpanded((prev) => !prev);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE.GOALS;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.programReflections.modal.goals;

  return (
    <div css={styles} className='d-flex flex-column text-left'>
      <div className='goals-header d-flex align-items-center'>
        <p className='flex-grow-1 text-large-body mb-0'>
          {translationBase.HEADER(total)}
        </p>
        <GoalsSectionIcon width={40} height={40} />
      </div>
      <div
        ref={listRef}
        className='goals-list d-flex flex-column'
        style={listMaxHeight != null ? { overflowY: 'auto', maxHeight: listMaxHeight } : undefined}
      >
        {visibleGoals.map((goal) => (
          <GoalItem
            key={goal.id}
            item={goal}
            isReadOnly
            isEditing={false}
            setEditingId={() => {}}
            connectionId={connectionId}
            enrollments={enrollments}
          />
        ))}
        <div ref={goalsEndRef} />
      </div>
      {hasMore && (
        <ClickableContainer
          className='toggle-show-more text-regular semi-bold gray-2 align-self-center'
          onClick={handleToggle}
          data-qa={isExpanded ? dataQATag.showLess : dataQATag.showMore}
          aria-expanded={isExpanded}
        >
          {isExpanded ? translationBase.SHOW_LESS() : translationBase.SHOW_MORE(hiddenCount)}
        </ClickableContainer>
      )}
    </div>
  );
};

export default ReflectionGoals;
